import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { View, ScrollView } from 'react-native';

import { DateDisplay } from '@/components/common/date-display';
import { TaskStatusBadge } from '@/components/tasks/status-badge';
import { Text, Button, IconButton, Modal, useTheme } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { useUser, useAssociation, useUpdateTask } from '@/hooks/use-data';
import { usePermissions } from '@/hooks/use-permissions';
import { TaskStatus } from '@/types/models';
import type { Task } from '@/types/models';
import { dueDayMs } from '@/utils/date-utils';

export const TaskDetailModal: React.FC<{
	task: Task | null;
	onDismiss: () => void;
}> = ({ task, onDismiss }) => {
	const theme = useTheme();
	const { user: currentUser } = useApi();
	const { data: assignedUser } = useUser(task?.assignedTo || '');
	const { data: createdByUser } = useUser(task?.createdBy || '');
	const { data: association } = useAssociation(task?.associationId || '');
	const updateTask = useUpdateTask();

	// Mirrors api/tasks.ts: the creator, or whoever holds UPDATE_USERS (Admin role / user_admin).
	const { canManageOthersTasks } = usePermissions();
	const canEdit = task && (task.createdBy === currentUser?.id || canManageOthersTasks);

	const handleStatusChange = async (status: TaskStatus) => {
		if (!task) return;
		try {
			await updateTask.mutateAsync({ id: task.id, data: { status } });
			onDismiss();
		} catch {
			// mutation error is surfaced by React Query's onError / global handler
		}
	};

	return (
		<Modal
			visible={!!task}
			onDismiss={onDismiss}
			contentContainerStyle={{ width: 480, maxHeight: '80%', padding: 0 }}
		>
			{task && (
				<ScrollView>
					<View style={{
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						paddingHorizontal: 16,
						paddingTop: 12,
					}}>
						<Text style={{ fontSize: 18, fontWeight: '700', color: theme.colors.onSurface }}>
							Task Details
						</Text>
						<View style={{ flexDirection: 'row', alignItems: 'center' }}>
							<IconButton
								icon="open-outline"
								onPress={() => { onDismiss(); router.push({ pathname: '/tasks/[id]', params: { id: task.id } }); }}
								size={20}
							/>
							<IconButton icon="close" onPress={onDismiss} size={20} />
						</View>
					</View>

					<View style={{ padding: 16, gap: 16 }}>
						<View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
							<Text style={{ fontSize: 20, fontWeight: 'bold', flex: 1, color: theme.colors.onSurface }}>
								{task.title}
							</Text>
							<TaskStatusBadge status={task.status} />
						</View>

						{task.description ? (
							<View>
								<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 4 }}>
									Description
								</Text>
								<Text style={{ fontSize: 15, color: theme.colors.onSurface }}>
									{task.description}
								</Text>
							</View>
						) : null}

						<View style={{ gap: 10 }}>
							{task.dueDate ? (
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
									<Ionicons name="calendar-outline" size={18} color={theme.colors.onSurfaceVariant} />
									<Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>Due: </Text>
									<DateDisplay date={dueDayMs(task.dueDate)} businessDate />
								</View>
							) : null}

							{assignedUser ? (
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
									<Ionicons name="person-outline" size={18} color={theme.colors.onSurfaceVariant} />
									<Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>
										Assigned to: {assignedUser.name}
									</Text>
								</View>
							) : null}

							{association ? (
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
									<Ionicons name="business-outline" size={18} color={theme.colors.onSurfaceVariant} />
									<Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>
										Association: {association.name}
									</Text>
								</View>
							) : null}

							{createdByUser ? (
								<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
									<Ionicons name="create-outline" size={18} color={theme.colors.onSurfaceVariant} />
									<Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>
										Created by: {createdByUser.name}
									</Text>
								</View>
							) : null}

							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
								<Ionicons name="time-outline" size={18} color={theme.colors.onSurfaceVariant} />
								<Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>
									Created: <DateDisplay date={new Date(task.createdAt)} dateOnly />
								</Text>
							</View>
						</View>

						{canEdit ? (
							<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8, justifyContent: 'space-between' }}>
								<View style={{ flexDirection: 'row', gap: 8 }}>
									<Button
										mode="outlined"
										onPress={() => handleStatusChange(TaskStatus.Cancelled)}
										loading={updateTask.isPending}
										compact
									>
										Cancel
									</Button>
									{task.status === TaskStatus.Archived ? (
										<Button
											mode="outlined"
											onPress={() => handleStatusChange(TaskStatus.Open)}
											textColor={theme.colors.success}
											style={{ borderColor: theme.colors.success }}
											loading={updateTask.isPending}
											compact
										>
										Re-open
										</Button>
									) : (
										<Button
											mode="outlined"
											onPress={() => handleStatusChange(TaskStatus.Archived)}
											textColor={theme.colors.error}
											style={{ borderColor: theme.colors.error }}
											loading={updateTask.isPending}
											compact
										>
										Archive
										</Button>
									)}
								</View>
								<Button
									mode="contained"
									onPress={() => handleStatusChange(TaskStatus.Completed)}
									buttonColor={theme.colors.success}
									textColor="#fff"
									loading={updateTask.isPending}
									compact
								>
									Complete
								</Button>
							</View>
						) : null}
					</View>
				</ScrollView>
			)}
		</Modal>
	);
};
